import { ReactNode } from "react";
import { getMich } from "../api";
import {
  LayoutDashboard, CalendarDays, BarChart3,
  Mail, Folder, Globe, Lightbulb, GitBranch, Monitor,
  MessageSquarePlus, ShieldCheck, SlidersHorizontal, Users, Zap,
} from "lucide-react";

// Each entry is rendered by the Sidebar component. `color` is a Catppuccin
// palette token (mauve | red | green | blue | …) that drives the icon and
// active-state accent.
//
// The nav is intentionally flat — every primary feature is one click from
// anywhere. Cross-section filtering happens *inside* each page (Calendar,
// Mail, Notes, Files all share the same DHBW/Private chip pattern)
// so the user never has to walk a tree to reach their content.

export type SidebarItem = {
  id: string;
  label: string;
  icon: ReactNode;
  path: string;
  /** Catppuccin token name, e.g. "mauve". Inherited from parent when omitted. */
  color?: string;
  children?: SidebarItem[];
  /** Reachable without signing in. Everything else is greyed out rather than
   *  hidden: an empty menu looks like a broken server, not a private one. */
  public?: boolean;
  /** Nur fuer den Verwalter. Ein Recht reicht dafuer nicht: „darf" gilt fuer
   *  jeden Verwalter *und* fuer jeden, dessen Auskunft noch unterwegs ist. */
  nurAdmin?: boolean;
  /** Nur, solange die Vollmacht gilt. Verwalter zu SEIN heisst hier nichts —
   *  erst wer sie sich geholt hat (Passwort und zweiter Faktor, auf Zeit),
   *  sieht diese Eintraege. Danach verschwinden sie wieder von selbst. */
  nurVollmacht?: boolean;
  /** Kein Ziel, sondern ein Griff: der Eintrag schlaegt etwas auf, statt
   *  irgendwohin zu fuehren. Die Hilfe ist so einer — sie gehoert zu der
   *  Seite, auf der man gerade steht, und haette als eigene Adresse ihren
   *  Zusammenhang verloren. */
  aktion?: "hilfe";
};

const ICON = "icon-svg";

// Each section gets its own Catppuccin token so the sidebar reads as a
// vertical color-key. The token also drives `--page-color` on the
// currently-active route (see Layout.tsx) — same hue tints page headings,
// so you always know "where you are" at a glance.
export const sidebarItems: SidebarItem[] = [
  { id: "dashboard", label: "Dashboard", icon: <LayoutDashboard className={ICON} />, path: "/",         color: "mauve",    public: true },
  { id: "calendar",  label: "Calendar",  icon: <CalendarDays    className={ICON} />, path: "/calendar", color: "blue"     },
  { id: "mail",      label: "Mail",      icon: <Mail            className={ICON} />, path: "/mail",     color: "peach"    },
  // Public notes are not their own entry but a filter inside this page —
  // signed out, the only one available.
  // Holds the public area as its own tab — hence visible without signing in,
  // and hence no second "container" entry next to it.
  { id: "storage",   label: "Storage",   icon: <Folder          className={ICON} />, path: "/storage",  color: "teal", public: true },
  { id: "grades",    label: "Grades",    icon: <BarChart3       className={ICON} />, path: "/grades",   color: "red"      },
  { id: "sites",     label: "Sites",     icon: <Globe           className={ICON} />, path: "/sites",    color: "sapphire", public: true },
  { id: "git",       label: "Git",       icon: <GitBranch       className={ICON} />, path: "/git",      color: "sky"      },
  { id: "lichter",   label: "Lights",    icon: <Lightbulb       className={ICON} />, path: "/lights",    color: "lavender" },
  { id: "pc",        label: "Machine",   icon: <Monitor         className={ICON} />, path: "/machine",       color: "flamingo" },
  // Automatik steht neben dem, was sie bedient — Lichter und Rechner. In den
  // Einstellungen stand sie falsch: „in zehn Minuten alles aus" ist eine
  // Handlung, keine Konfiguration.
  { id: "automation", label: "Automation", icon: <Zap            className={ICON} />, path: "/automation", color: "yellow" },
];

/** Items that live in the sidebar footer, above the sign-out button.
 *
 *  Security and Logs used to sit here as separate entries. Both are settings,
 *  so they are tabs inside /settings now — two rows less in the nav, nothing
 *  out of reach. */
export const sidebarFooterItems: SidebarItem[] = [
  // Die Verwaltung steht hier — aber erst, wenn die Vollmacht gilt.
  //
  // Ein dauerhafter Eintrag machte aus dem Verwalten etwas, das man im
  // Vorbeigehen anklickt. Gar kein Eintrag machte es unauffindbar: „i cant
  // see the users easily".
  //
  // Beides geht zusammen, wenn der Eintrag an die Vollmacht gebunden ist.
  // Geholt wird sie wie bisher in den Einstellungen unter Security, mit
  // Passwort und zweitem Faktor, auf Zeit. Solange sie gilt, stehen die
  // Verwalterseiten da, wo man sie erwartet; laeuft sie ab, verschwinden
  // sie wieder von selbst. Die Adresse /admin bleibt in jedem Fall, damit
  // Lesezeichen halten.
  {
    id: "admin",
    label: "Admin",
    icon: <ShieldCheck className={ICON} />,
    path: "/admin",
    color: "red",
    nurAdmin: true,
    nurVollmacht: true,
  },
  {
    id: "admin-users",
    label: "Users",
    icon: <Users className={ICON} />,
    path: "/admin?tab=people",
    color: "red",
    nurAdmin: true,
    nurVollmacht: true,
  },
  // Kein Eintrag „Help" mehr.
  //
  // Die Hilfe haengt jetzt an der Ueberschrift jeder Seite — ein Klick auf
  // „Git" oeffnet die Git-Hilfe. Ein zweiter Weg unten in der Leiste
  // oeffnete dasselbe Fenster noch einmal, nur weiter weg und ohne zu
  // wissen, wonach gefragt ist.
  {
    // Meldungen stehen unten neben den Einstellungen: man kommt nicht
    // deswegen her, aber wenn etwas nicht geht, will man es sofort
    // loswerden — und nicht erst suchen, wo man das hinschreibt.
    id: "reports",
    label: "Reports",
    icon: <MessageSquarePlus className={ICON} />,
    path: "/reports",
    color: "peach",
  },
  {
    id: "settings",
    label: "Settings",
    icon: <SlidersHorizontal className={ICON} />,
    path: "/settings",
    color: "green",
  },
];

/** Welche Berechtigung ein Eintrag braucht.
 *
 *  Steht bewusst hier und nicht an jedem Eintrag: die Namen kommen aus dem
 *  Server (config/permissions.go), und wer sie zusammen sieht, erkennt, was
 *  ein Verwalter vergeben kann. Was hier fehlt, ist für alle da. */
const BERECHTIGUNG: Record<string, string> = {
  calendar: "calendar",
  mail: "mail",
  storage: "files",
  grades: "grades",
  sites: "sites",
  git: "git",
  lichter: "lights",
  pc: "pc",
  automation: "settings",
  reports: "settings",
};

/** Was jemand in der Leiste sehen soll.
 *
 *  Zwei Fragen: angemeldet (sonst bleibt nur, was öffentlich ist) und
 *  berechtigt. Ausgegraut wäre eine Liste dessen, was man nicht darf —
 *  eine kürzere Leiste sagt schlicht, was es für einen gibt.
 *
 *  Das ist Höflichkeit, keine Sicherheit: jede Seite fragt den Server, und
 *  der prüft selbst. */
export function sichtbareEintraege(
  items: SidebarItem[],
  authed: boolean,
  darfFn: (perm: string) => boolean,
): SidebarItem[] {
  const ich = getMich();
  return items.filter((it) => {
    if (!authed && !it.public) return false;
    // „Nur der Verwalter" ist eine andere Frage als ein Recht: `darf`
    // antwortet mit Ja, solange die Auskunft ueber den Benutzer noch
    // unterwegs ist. Hier waere das ein Eintrag, der kurz aufblitzt und
    // wieder verschwindet.
    if (it.nurAdmin && !(authed && ich?.is_admin)) return false;
    // Die Vollmacht gilt auf Zeit. Ein Eintrag, der stehen bleibt, nachdem
    // sie abgelaufen ist, fuehrt auf eine Seite, die nach dem Passwort
    // fragt — und sieht dabei aus wie ein Fehler.
    if (it.nurVollmacht && !(authed && ich?.admin_mode?.active)) return false;
    const perm = BERECHTIGUNG[it.id];
    return !perm || !authed || darfFn(perm);
  });
}

/** Die Berechtigung zu einem Eintrag — für die Routen. */
export function berechtigungFuer(id: string): string | undefined {
  return BERECHTIGUNG[id];
}
